/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';

import { Menu } from './Menu';

/**
 * Returns the key from the item element
 */
const getKey = (item: HTMLElement) => {
  // Key is set on data-oj-menu-item-key on each menuItem. To get this key from the
  // the actual menuItem we need to do a kebab to camel-case conversion.
  return item.dataset['ojMenuItemKey'];
};

/**
 * A helper function that return the key of the first available item.
 */
export const getFirstVisibleKey = (root: HTMLElement | null) => {
  if (root) {
    const firstItem = root.querySelector('[data-oj-menu-item-key]');
    if (firstItem) {
      const key = getKey(firstItem as HTMLElement);
      if (key) {
        return key;
      }
    }
  }
  return null;
};

/**
 * A helper function that return the key of the last available item.
 */
export const getLastVisibleKey = (root: HTMLElement | null) => {
  if (root) {
    const items = root.querySelectorAll('[data-oj-menu-item-key]');
    const lastItem = items[items.length - 1];
    if (lastItem) {
      const key = getKey(lastItem as HTMLElement);
      if (key) {
        return key;
      }
    }
  }
  return null;
};

/**
 * A helper function that returns the key of the previous or the next item
 * given the specified current key
 */
export const getPrevNextKey = (root: HTMLElement | null, isPrev: boolean, currentKey?: string) => {
  return (): string | null => {
    if (root) {
      const items = Array.from(root.querySelectorAll('[data-oj-menu-item-key]')) as HTMLElement[];
      const currentKeyIndex = items.findIndex((item) => getKey(item) === currentKey);
      let nextIndex = currentKeyIndex + (isPrev ? -1 : 1);
      const itemsLastIndex = items.length - 1;
      if (nextIndex < 0) {
        nextIndex = itemsLastIndex;
      }
      if (nextIndex > itemsLastIndex) {
        nextIndex = 0;
      }
      return getKey(items[nextIndex]) || null;
    }
    return null;
  };
};

/**
 * A helper function to get the key from an element
 */
export const keyExtractor = (element: HTMLElement): string | null => {
  const item = element.closest('[data-oj-menu-item-key]');
  if (item) {
    const key = getKey(item as HTMLElement);
    return key === undefined ? null : key;
  }
  return null;
};

/**
 * Function to get the corresponding keyboard behavior to close the menu.
 */
export const getKeyboardCloseProps = (onClose: ComponentProps<typeof Menu>['onClose']) => {
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Tab') {
      onClose?.({ reason: 'tabbing' });
      event.preventDefault(); //TODO: Revisit this since we should let focus be at its natural flow, quick solution for now to focus correct item in main use case
    } else if (event.key === 'Escape') {
      onClose?.({ reason: 'cancel' });
    }
  };
  return { onKeyDown };
};

/**
 * Function to get the corresponding specific behavior for clicking/touching down menuContainer
 */
export const getMenuPointerDown = () => {
  const onPointerDown = (event: PointerEvent) => {
    //We just want to prevent default when element that was
    //pressed down was not a menu item. Using this flag removes issues
    //related to focus visibilty on "long press"
    const nextKey = keyExtractor(event.target as HTMLElement);
    if (!nextKey) {
      event.preventDefault();
    }
  };
  return { onPointerDown };
};

export type MenuValueUpdateDetail<T> = {
  previousValue?: T;
  value: T;
};
